// SPDX-License-Identifier: AGPL-3.0-or-later

import { Link, useNavigate } from 'react-router';
import { Button } from '@intakeform/ui';
import { useAutofillJobs, type AutofillStatus } from '../../api/autofill.js';
import { statusLabel } from './helpers.js';

const STATUSES: AutofillStatus[] = [
  'pending_admin_review',
  'pending_doctor_approval',
  'approved',
  'published',
  'rejected',
];

/**
 * Worklist of autofill jobs grouped by status. Each row links to the
 * appropriate stage (admin preview for `pending_admin_review`, doctor
 * approve for `pending_doctor_approval`, print view otherwise).
 */
export function AutofillListRoute() {
  const navigate = useNavigate();
  const jobs = useAutofillJobs();

  if (jobs.isLoading) return <p className="muted">Loading…</p>;
  if (jobs.error) return <div className="error-banner">Failed to load autofill jobs.</div>;

  const grouped = new Map<AutofillStatus, typeof jobs.data>();
  for (const s of STATUSES) grouped.set(s, []);
  for (const j of jobs.data ?? []) {
    const arr = grouped.get(j.status as AutofillStatus);
    if (arr) arr.push(j);
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Autofill jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Forms prefilled from EMR data, awaiting review or already published.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => navigate('/autofill/new')}
          data-testid="autofill-new-cta"
        >
          New autofill job
        </Button>
      </header>

      <div className="space-y-6">
        {STATUSES.map((status) => {
          const rows = grouped.get(status) ?? [];
          if (rows.length === 0) return null;
          return (
            <section key={status}>
              <h2 className="text-sm font-semibold uppercase text-muted-foreground tracking-wide mb-2">
                {statusLabel(status)} ({rows.length})
              </h2>
              <ul className="divide-y divide-border border border-border rounded-md">
                {rows.map((j) => {
                  const link =
                    j.status === 'pending_admin_review'
                      ? `/autofill/${j.id}/preview`
                      : j.status === 'pending_doctor_approval'
                        ? `/autofill/${j.id}/approve`
                        : `/autofill/${j.id}/print`;
                  return (
                    <li key={j.id}>
                      <Link
                        to={link}
                        className="flex items-center justify-between p-3 hover:bg-neutral-50/50 dark:hover:bg-neutral-800/10"
                        data-testid={`autofill-row-${j.id}`}
                      >
                        <div>
                          <div className="font-medium">
                            {j.emrContextSnapshot.patient.displayName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {j.patientRef} · created {new Date(j.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {j.prefillFindings.length > 0
                            ? `${String(j.prefillFindings.length)} need input`
                            : 'fully prefilled'}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        {(jobs.data ?? []).length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-xl">
            <p className="text-muted-foreground">No autofill jobs yet.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
